"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { ConfigsMap } from "@/lib/remote-config";

/**
 * React context that exposes remote config values to any client
 * component under the marketing layout, avoiding prop drilling.
 *
 * The marketing layout fetches the configs once on the server (Next.js
 * caches the request) and threads them through this provider. Client
 * components consume the map via {@link useConfigs}.
 *
 * @example
 * ```tsx
 * "use client";
 * import { useConfigs } from "./LandingConfigsProvider";
 * import { getConfigString } from "@/lib/remote-config";
 *
 * export function Headline() {
 *   const configs = useConfigs();
 *   const text = getConfigString(configs, "hero_headline", "Welcome");
 *   return <h1>{text}</h1>;
 * }
 * ```
 */

const LandingConfigsContext = createContext<ConfigsMap>({});

interface Props {
  configs: ConfigsMap;
  children: ReactNode;
}

export function LandingConfigsProvider({ configs, children }: Props) {
  return (
    <LandingConfigsContext.Provider value={configs}>
      {children}
    </LandingConfigsContext.Provider>
  );
}

/** Reads the configs map from context. Returns `{}` outside the provider. */
export function useConfigs(): ConfigsMap {
  return useContext(LandingConfigsContext);
}
